import type { ProblemDetails } from '@portal/kernel';

/** Error thrown by `apiFetch` for non-2xx responses; carries the RFC 9457 problem body when there is one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails | null,
  ) {
    super(problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.problem?.code ?? `core.http_${this.status}`;
  }
}

/**
 * Fetches `/api/v1/<path>` with the session cookie and parses JSON.
 * Network failures surface as `ApiError` with status 0 so pages can show one consistent error state.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith('/api/') ? path : `/api/v1/${path.replace(/^\//, '')}`;
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body !== undefined && !(init.body instanceof FormData))
    headers.set('content-type', 'application/json');

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, null);
  }

  if (!response.ok) {
    const isProblem = response.headers.get('content-type')?.includes('json') ?? false;
    const problem = isProblem
      ? ((await response.json().catch(() => null)) as ProblemDetails | null)
      : null;
    throw new ApiError(response.status, problem);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
